/**
 * Sharing a board task into chat.
 *
 * Deliberately unlike the workspace/collection/request shares in chatShare.js,
 * for two reasons.
 *
 * NO TOKEN. Those mint a recipient-scoped `share_links` row so someone outside
 * the resource can be granted access. A task must not work that way: access to
 * a task IS team membership, and a share link would be a second, parallel way
 * in — one that outlives the team roster and cannot be revoked by removing
 * someone from the team.
 *
 * NO CONTENT. `chat_messages.body` is Cloak-encrypted; `metadata` is a plain
 * `:map`. The task's title and description live in `extension_documents.data`,
 * which is also encrypted. Copying a title into the share metadata would
 * decrypt board content and re-store it in the clear, where it outlives the
 * task and lands in every dump — the same defect that had to be fixed in the
 * board notifications. So the card carries a POINTER, and each recipient
 * resolves the title from the copy of the board they already sync.
 *
 * The consequence is the good one: a recipient who is not on the team resolves
 * nothing and sees "no access", without the server having to decide anything.
 */

export const TASK_SHARE_TYPE = 'task';

/**
 * The chat message metadata for a shared task. Ids only.
 *
 * `board_id` and `team_id` ride along because the recipient needs to know
 * WHICH board to look in — the task id alone is meaningless without its scope,
 * and searching every board for it would be both slow and a way to probe for
 * ids the reader cannot see.
 */
export function buildTaskShareMetadata({ task, board }) {
  if (!task?.id || !board?.id) return null;
  // A private board has no team, so there is nobody a share could reach who
  // is not already the author. Refusing here rather than in the UI means no
  // caller can produce an unopenable card.
  if (board.visibility !== 'team' || board.teamId == null) return null;

  return {
    share_type: TASK_SHARE_TYPE,
    task_id: String(task.id),
    board_id: String(board.id),
    team_id: String(board.teamId),
    v: 1,
  };
}

/** Is this a task share card? */
export function isTaskShare(metadata) {
  return metadata?.share_type === TASK_SHARE_TYPE;
}

function memberIdsOf(channel) {
  return (channel?.members || [])
    .map((m) => (m?.user_id != null ? String(m.user_id) : m?.id != null ? String(m.id) : null))
    .filter(Boolean);
}

/**
 * The conversations this task may be sent to.
 *
 * TWO rules, and both are restrictions rather than conveniences.
 *
 * 1. DIRECT MESSAGES ONLY. Sending to a channel is deliberately not offered
 *    yet. A channel's membership changes without the sender being involved —
 *    someone added tomorrow inherits every card posted yesterday, and there is
 *    no moment at which anyone decided that. A DM has exactly one recipient
 *    who is known at the instant you send, which is a promise the product can
 *    actually keep. Channels can come back once there is a story for what
 *    happens to old cards when the membership changes.
 *
 * 2. EVERY PARTICIPANT ON THE TEAM. Not "at least one team member", and not
 *    "the sender is on the team". A conversation containing one outsider is a
 *    conversation where the outsider sees the card — and while they could not
 *    open the task, the card still announces that a task exists, in a
 *    conversation they may have no business knowing about.
 *
 * A conversation whose member list has not loaded is EXCLUDED, not included:
 * an empty `members` array is missing data, and defaulting missing data to
 * "everyone here is allowed" is how this rule would quietly stop meaning
 * anything.
 */
export function channelsForTeamShare(channels, teamMemberIds) {
  const allowed = new Set((teamMemberIds || []).map(String));
  if (allowed.size === 0) return [];

  return (channels || []).filter((c) => {
    if (c?.kind !== 'dm') return false;
    const ids = memberIdsOf(c);
    if (ids.length === 0) return false;
    return ids.every((id) => allowed.has(id));
  });
}

/**
 * Why a conversation was rejected, for the empty state. Users read "no
 * conversations" as a broken picker; they read "these all include people
 * outside the team" as a rule.
 */
export function excludedReason(channel, teamMemberIds) {
  if (channel?.kind !== 'dm') return 'not-a-dm';
  const allowed = new Set((teamMemberIds || []).map(String));
  const ids = memberIdsOf(channel);
  if (ids.length === 0) return 'members-unknown';
  return ids.every((id) => allowed.has(id)) ? null : 'outside-team';
}
